import type { ZuoraPaymentMethod } from '@kimuno/api-sdk/lib/services/billing';
import type { GetProductsOptions } from '@kimuno/api-sdk/lib/services/product';
import type { PaymentPeriod } from '@kimuno/api-sdk/lib/types/common';
import type { SortByOption } from '@kimuno/api-sdk/lib/types/requests/Common';
import type { ContentInterface } from '@kimuno/api-sdk/lib/types/requests/Track';
import type { CollectionColor } from '@kimuno/api-sdk/lib/types/responses/Collection';
import type { Course } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import config from 'config';
import * as isoCountries from 'i18n-iso-countries';
import deCountries from 'i18n-iso-countries/langs/de.json';

import type { Environment as AmplitudeProjectEnvironment } from './ampli';
import { borderRadius, palette } from './assets/styles/themes/tokens';
import type { SearchFilters } from './types/search';
import { arrayToObject } from './utils/arrayToObject';

export interface RouteDescriptor {
	documentTitle?: string;
	optionalParam?: string;
	param?: string;
	root: string;
}

const neuHost = config.urls.neu;

// Platform root (the "neu" app). Prod: https://app.meinunterricht.de/.
export const platformUrl = neuHost;

// date-fns uses Unicode tokens for formatting (https://date-fns.org/v2.25.0/docs/format)
export const dateFormat = 'dd.MM.yyyy';
export const dateTimeFormat = 'dd.MM.yyyy, HH:mm';

export const subscriptionDateFormat = 'd. MMMM yyyy';
// SUB-89: trial-end tile and cancel-during-trial toast render the exact
// timestamp Stripe ends the trial at, so the user can't misread the date as
// "23:59 that day". Literal 'Uhr' is escaped per date-fns Unicode token rules.
export const trialEndDateTimeFormat = "d. MMMM yyyy, HH:mm 'Uhr'";

export const readPageTime = 30000; // 30 seconds

export const zipRetryTime = 1000; // 1 second

export const tooltipEnterDelay = 400; // 0.4 seconds
export const tooltipLeaveDelay = 500; // 0.5 seconds

export const alertDuration = 3000; // 3 seconds

export const defaultSearchFilters: SearchFilters = {
	term: '',
	sortBy: 'relevance',
	contentType: ['document', 'video'],
};

// Preserves anchor written by the removed LB Welcome dialog so users who already dismissed it
// keep their existing 7-day gap state instead of being re-anchored from now.
export const lbReOnboardingAnchorKey = 'onboarding-lehrerburo-dialog-date';
export const LB_REONBOARDING_GAP_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

export const externalLinks = {
	dataPolicy: 'https://www.meinunterricht.de/datenschutz/',
	faq: config.links.faq,
	assistantFaq: config.links.assistantFaq,
	assistantLoomVideo:
		'https://www.loom.com/embed/e6290ff2e92a4c678d4c6184c6e905be?sid=11236fdc-0709-40b6-a6ea-140d1007dcfe',
	tac: 'https://www.meinunterricht.de/agb/',
	premiumPackageSelection: 'https://www.meinunterricht.de/registrierung/',
	tacPdf: 'https://www.meinunterricht.de/wp-content/uploads/2019/02/media/pdf/2019-02-28_AGB_mU.pdf',
	schoolLicenceOffer: 'https://schulen.meinunterricht.de/premium',
	schoolLicence360Offer: 'https://schulen.meinunterricht.de/meinunterricht360',
	widerrufsbelehrung: 'https://www.meinunterricht.de/widerrufsbelehrung/',
	licenceLinks: {
		'CC0 1.0': 'https://creativecommons.org/publicdomain/zero/1.0/deed.de',
		'CC BY 1.0': 'https://creativecommons.org/licenses/by/1.0/deed.de',
		'CC BY 2.0': 'https://creativecommons.org/licenses/by/2.0/deed.de',
		'CC BY 3.0': 'https://creativecommons.org/licenses/by/3.0/deed.de',
		'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/deed.de',
		'CC BY-SA 1.0': 'https://creativecommons.org/licenses/by-sa/1.0/deed.de',
		'CC BY-SA 2.0': 'https://creativecommons.org/licenses/by-sa/2.0/deed.de',
		'CC BY-SA 3.0': 'https://creativecommons.org/licenses/by-sa/3.0/deed.de',
		'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/deed.de',
		'CC BY-ND 1.0': 'https://creativecommons.org/licenses/by-nd/1.0/deed.de',
		'CC BY-ND 2.0': 'https://creativecommons.org/licenses/by-nd/2.0/deed.de',
		'CC BY-ND 3.0': 'https://creativecommons.org/licenses/by-nd/3.0/deed.de',
		'CC BY-ND 4.0': 'https://creativecommons.org/licenses/by-nd/1.0/deed.de',
		'CC BY-NC-SA 4.0': 'https://creativecommons.org/licenses/by-nc-sa/4.0',
	},
	imprint: 'https://www.meinunterricht.de/impressum/',
	register: 'https://www.meinunterricht.de/registrierung/?source=organic',
	hilfe: 'https://hilfe.meinunterricht.de/de/',
};

export const kontoErstellenPath = '/registrierung/konto-erstellen';

export const emailAddresses = {
	support: 'support@meinunterricht.de',
	schoolSupport: 'schulen@meinunterricht.de',
};

export enum LoginSources {
	facebook = 'facebook',
	email = 'email',
}

export const paymentMethods: { [key in ZuoraPaymentMethod]: { name: string } } = {
	sepa: {
		name: 'SEPA',
	},
	creditCard: {
		name: 'CreditCard',
	},
	braintree: {
		name: 'Paypal',
	},
	invoice: {
		name: 'Rechnung',
	},
};

export const licenceCategory = {
	BASIS: 'basis',
	FREE: 'free',
	LIBRARY: 'library',
	SCHOOL: 'school',
	STUDENT: 'student',
	TEACHER: 'teacher',
	TRAINEE: 'trainee',
	TUITION: 'tuition',
};

export const SCHOOL_TRIAL_LICENCE = '24';

export const paymentRhythmDisplayNames: { [key in PaymentPeriod]: string } = {
	fullAmount: 'Total amount',
	quarterly: 'Quarterly',
	monthly: 'Monthly',
	annually: 'Yearly',
};

export const suspensionOptions = [
	{ label: '1 Monat', value: '1' },
	{ label: '2 Monate', value: '2' },
	{ label: '3 Monate', value: '3' },
];

export const errorCodes = {
	ipRestriction: 1003,
	trialAlreadyUsed: 1106,
	userNotValidated: 1204,
	fbEmailDoesntExist: -1,
	emailAlreadyUsed: 1101,
};

export const routes = {
	onboardingPage: {
		documentTitle: 'Konto erstellen',
		root: 'konto-erstellen',
	},
	lernsetsEditor: {
		documentTitle: 'meinUnterricht - Lernset Editor',
		root: 'lernsets/:lernsetId/editor/:partId',
	},
	learnsetsDashboard: {
		documentTitle: 'meinUnterricht - Lernset Dashboard',
		root: 'lernsets',
	},
	lernsetsOverview: {
		documentTitle: 'meinUnterricht - Lernset Overview',
		root: 'lernsets',
		param: 'lernsetId',
	},
	lernsetsPublicView: {
		documentTitle: 'meinUnterricht — Öffentliche Ansicht',
		root: 'set-oeffentlich',
		param: 'accessToken',
	},
	collections: {
		documentTitle: 'meinUnterricht - Kollektionen',
		root: 'kollektion',
	},
	changeEmailConfirm: {
		documentTitle: 'meinUnterricht - Neue Email',
		root: 'neue-email-bestaetigen',
	},
	curatedCollections: {
		documentTitle: 'meinUnterricht - Kuratierte Kollektionen',
		root: 'kuratierte-kollektionen',
	},
	dashboard: {
		documentTitle: 'meinUnterricht - Startseite',
		root: 'start',
	},
	aiTools: {
		documentTitle: 'meinUnterricht - KI Tools',
		root: 'ki-tools',
		optionalParam: 'conversationId',
	},
	aiAssistantHistory: {
		documentTitle: 'meinUnterricht - Verlauf',
		root: 'verlauf',
	},
	downloads: {
		documentTitle: 'meinUnterricht - Downloads',
		root: 'downloads',
	},
	myContents: {
		documentTitle: 'meinUnterricht - My Contents',
		root: 'meine-inhalte',
	},
	schoolAdmin: {
		documentTitle: 'meinUnterricht - School Admin',
		root: 'school/dashboard',
	},
	schoolAdminLicence: {
		documentTitle: 'meinUnterricht - School Admin Dashboard',
		root: 'school/license',
	},
	schoolAdminTeachers: {
		documentTitle: 'meinUnterricht - School Admin Teachers',
		root: 'school/teachers',
	},
	stripeSchoolAdminTeachers: {
		documentTitle: 'meinUnterricht - School Admin Teachers',
		root: 'stripe-school/teachers',
	},
	schoolAdminList: {
		documentTitle: 'meinUnterricht - School Admins List',
		root: 'school/admins',
	},
	quickDownload: {
		documentTitle: 'meinUnterricht - Download',
		root: 'download-schnell',
	},
	followed: {
		documentTitle: 'meinUnterricht - Folge ich',
		root: 'folge-ich',
	},
	pinned: {
		documentTitle: 'meinUnterricht - Merkliste',
		root: 'merkliste',
	},
	login: {
		documentTitle: 'Anmelden',
		root: 'login',
	},
	keycloakRedirect: {
		documentTitle: 'Anmelden',
		root: 'sso-redirect',
	},
	personalDetails: {
		documentTitle: 'Meine Daten',
		root: 'meine-daten',
		optionalParam: 'section',
	},
	productPage: {
		root: 'dokument-uebersicht',
		param: 'productId',
	},
	productAttachmentsPage: {
		documentTitle: 'meinUnterricht - Zusatzmaterialien',
		root: 'zusatzmaterialien',
	},
	resetPassword: {
		documentTitle: 'Erinnere mich an mein Passwort',
		root: 'passwort-vergessen',
	},
	resetPasswordConfirmed: {
		documentTitle: 'E-Mail gesendet',
		root: 'email-mit-link-versendet',
	},
	setPassword: {
		documentTitle: 'Passwort festlegen',
		root: 'neues-passwort-festlegen',
	},
	checkoutSuccess: {
		documentTitle: 'Zahlung erfolgreich',
		root: 'checkout/success',
	},
	payment: {
		documentTitle: 'Zahlung',
		root: 'payment',
	},
	deprecatedUpgradeConfirm: {
		documentTitle: 'Bestellung',
		root: 'bestellung',
	},
	deprecatedUpgradeOfferCode: {
		documentTitle: 'Angebot',
		root: 'angebote',
		param: 'upgradeCode',
	},
	upgradePackageSelection: {
		documentTitle: 'Paketauswahl',
		root: 'premium',
	},
	stripeUpgrade: {
		documentTitle: 'Tarif wechseln',
		root: 'upgrade-options',
	},
	stripeUpgradeConfirm: {
		documentTitle: 'Zahlung',
		root: 'bestaetigen',
	},
	stripeUpgradeCheckout: {
		documentTitle: 'Zahlung',
		root: 'checkout',
	},
	upgradePayment: {
		documentTitle: 'Zahlungsdaten',
		root: 'zahlungsdaten',
	},
	deprecatedUpgradeRef: {
		documentTitle: 'Premium für Referendare',
		root: 'ref',
	},
	deprecatedPremiumTrialRegistration: {
		documentTitle: 'meinUnterricht | Gratis Registrierung',
		root: 'registrierung-gratis',
	},
	register: {
		documentTitle: 'Registrierung',
		root: 'registrierung',
	},
	singlePagePayment: {
		documentTitle: 'Zahlungsdetails',
		root: 'Zahlungsdetails',
	},
	deprecatedRegisterPremiumPackageSelection: {
		documentTitle: 'Premium',
		root: 'premium',
	},
	registerPremiumCreateAccount: {
		documentTitle: 'Konto erstellen',
		root: 'konto-erstellen',
	},
	deprecatedRegisterPremiumPayment: {
		documentTitle: 'Zahlungsart',
		root: 'zahlungsart',
	},
	deprecatedRegisterPremiumConfirm: {
		documentTitle: 'Bestätigung',
		root: 'bestaetigung',
	},
	ipRangeError: {
		documentTitle: 'Außerhalb der Bibliotheks-IPs',
		root: 'ip-error',
	},
	setNewEmail: {
		documentTitle: 'E-Mail Adresse ändern',
		root: 'email-aendern',
	},
	reader: {
		root: 'dokument',
	},
	publicReader: {
		root: 'dokument-schueleransicht',
	},
	generatedDocumentPublicView: {
		documentTitle: 'meinUnterricht — Öffentliche Ansicht',
		root: 'dokument-oeffentlich',
		param: 'accessToken',
	},
	podcastTranscriptPublicView: {
		documentTitle: 'meinUnterricht — Öffentliche Ansicht',
		root: 'transkript-oeffentlich',
		param: 'accessToken',
	},
	search: {
		documentTitle: 'Suche',
		root: 'suche',
		optionalParam: 'tab',
	},
	courseCatalog: {
		documentTitle: 'meinUnterricht - Kurse',
		root: 'kurskatalog',
	},
	courseCover: {
		documentTitle: 'meinUnterricht - Kurs Overview',
		root: 'kurskatalog',
		param: 'courseId',
	},
	coursePlayer: {
		documentTitle: 'meinUnterricht - Kurse Player',
		root: 'kurskatalog/:courseId/kurse',
	},
	myCourses: {
		documentTitle: 'meinUnterricht - Meine Kurse',
		root: 'meinekurse',
	},
	myCertificates: {
		documentTitle: 'meinUnterricht - Zertifikate',
		root: 'zertifikate',
	},
	courseFinishPage: {
		documentTitle: 'meinUnterricht - Kurs Abschluss',
		root: 'kurskatalog/abschluss',
		param: 'scormCourseId',
	},
	courseAttachments: {
		documentTitle: 'meinUnterricht - Kurs Zusatzmaterialien',
		root: 'kurskatalog/:courseId/zusatzmaterialien',
	},
	notFound: {
		root: '404',
	},
};

export const coursePages: Array<{
	root: string;
	documentTitle: string;
	optionalParam?: string;
	param?: string;
}> = [
	routes.courseCatalog,
	routes.courseCover,
	routes.coursePlayer,
	routes.myCourses,
	routes.myCertificates,
	routes.courseFinishPage,
	routes.courseAttachments,
];

export const sharingLinks = {
	curatedCollection: `${neuHost}/${routes.curatedCollections.root}`,
	mindItem: `${neuHost}/${routes.reader.root}`,
	product: `${neuHost}/${routes.productPage.root}`,
	lernsets: `${neuHost}/${routes.lernsetsPublicView.root}`,
	course: `${neuHost}/${routes.courseCatalog.root}`,
	generatedDocument: `${neuHost}/${routes.generatedDocumentPublicView.root}`,
	podcastTranscript: `${neuHost}/${routes.podcastTranscriptPublicView.root}`,
};

// Please note that a user type can be b2c or b2b (eg school user).
// This difference is not reflected in userType but it can be important!
// For example, a school user cannot upgrade their own subscription (this is done by their school).
export const userType = {
	basis: 1,
	document: 2,
	courses: 3,
	kombi: 4,
	aiOnly: 5,
};

export const premiumUserTypes = [
	userType.document,
	userType.courses,
	userType.kombi,
	userType.aiOnly,
];

export const defaultTermLength = '12';

// TODO: FD-2549 Remove this constant.
export const singlePageRegistrationLicences = [283, 285, 292, 293];

export const classOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

export const isTraineeOptions = [
	{
		value: true,
		text: 'Yes',
	},
	{
		value: false,
		text: 'No',
	},
	{
		value: undefined,
		text: 'Not specified',
	},
];

export type Subject = {
	id: string;
	isTeachable: boolean;
	text: string;
	taxonomy: string;
	popularity: number;
	englishTranslation?: string;
	iconName?: string;
};

export const NONE_OF_THE_ABOVE_OPTION = 'Andere';

const defaultConstants = {
	gtmId: config.gtm.id,
	loginUrl: `${neuHost}/${routes.login.root}`,
	learningFormats: [
		{
			value: 'Experimentieren',
			text: 'Experimentieren',
		},
		{
			value: 'Gespräch führen',
			text: 'Gespräch führen',
		},
		{
			value: 'Gestalten',
			text: 'Gestalten',
		},
		{
			value: 'Gruppenarbeit/Partnerarbeit',
			text: 'Gruppenarbeit / Partnerarbeit',
		},
		{
			value: 'Kontrollieren / Prüfen',
			text: 'Kontrollieren/prüfen',
		},
		{
			value: 'Konzentrieren / Meditieren / Entspannen',
			text: 'Konzentrieren / meditieren / entspannen',
		},
		{
			value: 'Lernspiel spielen',
			text: 'Lernspiel spielen',
		},
		{
			value: 'Recherchieren / Dokumentieren',
			text: 'Recherchieren / dokumentieren',
		},
		{
			value: 'Sich bewegen',
			text: 'Sich bewegen',
		},
		{
			value: 'Simulationsspiel durchführen',
			text: 'Simulationsspiel durchführen',
		},
		{
			value: 'Still arbeiten',
			text: 'Still arbeiten',
		},
		{
			value: 'Szenisch arbeiten',
			text: 'Szenisch arbeiten',
		},
		{
			value: 'Vortragen / Präsentieren',
			text: 'Vortragen / präsentieren',
		},
	],
	subjects: [
		{
			id: 'Biologie',
			text: 'Biologie',
			taxonomy: 'Biologie_neu',
			isTeachable: true,
			popularity: 6,
			englishTranslation: 'Biology',
			iconName: 'biology',
		},
		{
			id: 'Chemie',
			text: 'Chemie',
			taxonomy: 'Chemie_neu',
			isTeachable: true,
			popularity: 12,
			englishTranslation: 'Chemistry',
			iconName: 'chemistry',
		},
		{
			id: 'Deutsch',
			text: 'Deutsch',
			taxonomy: 'Deutsch_neu',
			isTeachable: true,
			popularity: 1,
			englishTranslation: 'German',
			iconName: 'german',
		},
		// Here we're using a double url encoded value for DaZ, as Roboto is expecting this in the search url
		{
			id: 'DaZ',
			text: 'Deutsch als Zweitsprache',
			taxonomy: 'Deutsch%2520als%2520Zweitsprache',
			isTeachable: true,
			popularity: 16,
			englishTranslation: 'German as a second language',
			iconName: 'daz',
		},
		{
			id: 'Englisch',
			text: 'Englisch',
			taxonomy: 'Englisch_neu',
			isTeachable: true,
			popularity: 7,
			englishTranslation: 'English',
			iconName: 'english',
		},
		{
			id: 'Philosophie',
			text: 'Ethik & Philosophie',
			taxonomy: 'Religion-Ethik_neu',
			isTeachable: true,
			popularity: 5,
			englishTranslation: 'Ethics & Philosophy',
			iconName: 'philosophy',
		},
		{
			id: 'Fächerübergreifend',
			text: 'Fächerübergreifend',
			taxonomy: 'Fächerübergreifend',
			isTeachable: false,
			popularity: 23,
			englishTranslation: 'Interdisciplinary',
			iconName: 'interdisciplinary',
		},
		{
			id: 'Französisch',
			text: 'Französisch',
			taxonomy: 'Französisch_neu',
			isTeachable: true,
			popularity: 19,
			englishTranslation: 'French',
			iconName: 'french',
		},
		{
			id: 'Erdkunde',
			text: 'Geografie / Erdkunde',
			taxonomy: 'Erdkunde_neu',
			isTeachable: true,
			popularity: 11,
			englishTranslation: 'Geography',
			iconName: 'geography',
		},
		{
			id: 'Geschichte',
			text: 'Geschichte',
			taxonomy: 'Geschichte_neu',
			isTeachable: true,
			popularity: 3,
			englishTranslation: 'History',
			iconName: 'history',
		},
		{
			id: 'Hauswirtschaft',
			text: 'Hauswirtschaft',
			taxonomy: 'Hauswirtschaft',
			isTeachable: true,
			popularity: 22,
			englishTranslation: 'Home Economics',
			iconName: 'homeEconomics',
		},
		{
			id: 'Informatik',
			text: 'Informatik',
			taxonomy: 'Informatik',
			isTeachable: true,
			popularity: 25,
			englishTranslation: 'Computer Science',
			iconName: 'computerScience',
		},
		{
			id: 'Kunst',
			text: 'Kunst',
			taxonomy: 'Kunst',
			isTeachable: true,
			popularity: 10,
			englishTranslation: 'Art',
			iconName: 'art',
		},
		{
			id: 'Latein',
			text: 'Latein',
			taxonomy: 'Latein_neu',
			isTeachable: true,
			popularity: 21,
			englishTranslation: 'Latin',
			iconName: 'latin',
		},
		{
			id: 'Mathematik',
			text: 'Mathematik',
			taxonomy: 'Mathematik_neu',
			isTeachable: true,
			popularity: 2,
			englishTranslation: 'Mathematics',
			iconName: 'mathematics',
		},
		{
			id: 'Didaktik-Methodik',
			text: 'Methodik / Didaktik',
			taxonomy: 'Didaktik-Methodik_neu',
			isTeachable: false,
			popularity: 24,
			englishTranslation: 'Didactics',
			iconName: 'didactics',
		},
		{
			id: 'Musik',
			text: 'Musik',
			taxonomy: 'Musik_neu',
			isTeachable: true,
			popularity: 14,
			englishTranslation: 'Music',
			iconName: 'music',
		},
		{
			id: 'Naturwissenschaften',
			text: 'Naturwissenschaften',
			taxonomy: 'Naturwissenschaften',
			isTeachable: true,
			popularity: 18,
			englishTranslation: 'Natural Sciences',
			iconName: 'naturalSciences',
		},
		{
			id: 'Physik',
			text: 'Physik',
			taxonomy: 'Physik_neu',
			isTeachable: true,
			popularity: 15,
			englishTranslation: 'Physics',
			iconName: 'physics',
		},
		{
			id: 'Politik',
			text: 'Politik',
			taxonomy: 'Politik_neu',
			isTeachable: true,
			popularity: 9,
			englishTranslation: 'Politics',
			iconName: 'politics',
		},
		{
			id: 'Religion-Ethik',
			text: 'Religion',
			taxonomy: 'Religion-Ethik_neu',
			isTeachable: true,
			popularity: 4,
			englishTranslation: 'Religion',
			iconName: 'religion',
		},
		{
			id: 'Sachkunde',
			text: 'Sachunterricht',
			taxonomy: 'Sachunterricht_neu',
			isTeachable: true,
			popularity: 8,
			englishTranslation: 'General studies',
			iconName: 'generalStudies',
		},
		{
			id: 'Spanisch',
			text: 'Spanisch',
			taxonomy: 'Spanisch_neu',
			isTeachable: true,
			popularity: 20,
			englishTranslation: 'Spanish',
			iconName: 'spanish',
		},
		{
			id: 'Sport',
			text: 'Sport',
			taxonomy: 'Sport_neu',
			isTeachable: true,
			popularity: 13,
			englishTranslation: 'Physical Education',
			iconName: 'physicalEducation',
		},
		{
			id: 'Wirtschaft',
			text: 'Wirtschaft',
			taxonomy: 'Wirtschaft',
			isTeachable: true,
			popularity: 17,
			englishTranslation: 'Economics',
			iconName: 'economics',
		},
		{
			id: NONE_OF_THE_ABOVE_OPTION,
			text: 'Keine dieser Optionen',
			isTeachable: true,
			popularity: 26,
		},
	] as Subject[],
	courseTypes: [
		{
			value: 'Kurs',
			text: 'Kurs',
		},
		{
			value: 'Impuls',
			text: 'Impuls',
		},
		{
			value: 'Webinar',
			text: 'Webinar-Aufzeichnung',
		},
	],
	courseThemes: [
		{
			value: 'Schulentwicklung & Außerunterrichtliches',
			text: 'Schulentwicklung & Außerunterrichtliches',
		},
		{
			value: 'Persönlichkeit & Gesundheit',
			text: 'Persönlichkeit & Gesundheit',
		},
		{
			value: 'Digitales',
			text: 'Digitales',
		},
		{
			value: 'Schulrecht',
			text: 'Schulrecht',
		},
		{
			value: 'Methodik & Didaktik',
			text: 'Methodik & Didaktik',
		},
		{
			value: 'Pädagogik',
			text: 'Pädagogik',
		},
		{
			value: 'Team- & Elternarbeit',
			text: 'Team- & Elternarbeit',
		},
	],
	courseDuration: [
		{ text: 'Bis 5 Min.', value: '5' },
		{ text: '5 bis 15 Min.', value: '5-15' },
		{ text: '15 bis 30 Min.', value: '15-30' },
		{ text: '30 bis 45 Min.', value: '30-45' },
		{ text: '45 bis 90 Min.', value: '45-90' },
		{ text: '90 bis 120 Min.', value: '90-120' },
		{ text: '120+ Min.', value: '120' },
	],
};

export default {
	...defaultConstants,
	subjectsById: arrayToObject(defaultConstants.subjects, (s) => s.id),
};

export const collectionsSettings = {
	colors: {
		default: 'red' as const,
		options: ['red', 'blue', 'green', 'orange', 'purple', 'yellow'] as CollectionColor[],
	},
};

export const CAROUSEL_DEFAULT_LENGTH = 25;

export const PINNED_CAROUSEL_LOWER_LIMIT = 2;

export const CAROUSEL_DEFAULT_SUBJECT_ID = 'Didaktik-Methodik';

export const CAROUSEL_DEFAULT_QUERY_OPTIONS: GetProductsOptions = {
	limit: CAROUSEL_DEFAULT_LENGTH,
	sort: 'desc',
	sortBy: 'createdDate',
};

export const pagination = {
	itemsOnPage: 25,
};

export const loadOnRefreshLimit = 60;

type SearchSettings = {
	itemsPerPage: number;
	defaultSortBy: SortByOption;
	maxOffset: number;
};

export const searchSettings: SearchSettings = {
	itemsPerPage: 12,
	defaultSortBy: 'relevance',
	maxOffset: 9950,
};

type ConversationListSettings = {
	itemsPerPage: number;
	maxOffset: number;
};

export const conversationListSettings: ConversationListSettings = {
	itemsPerPage: 20,
	maxOffset: 9999,
};

export const zoomOptions = {
	zoomStep: 10,
	maxZoom: 200,
	minZoom: 50,
	defaultZoom: 90,
};

export const studyflix = {
	url: 'https://studyflix.de/embed',
	dataPartner: 'cG72Ik5A',
};

isoCountries.registerLocale(deCountries);
export const countries = isoCountries.getNames('de', { select: 'official' });

// Maps Node Environment to Amplitude Project Environment
export const mapNodeEnvToAmpliEnv: Record<string, AmplitudeProjectEnvironment> = {
	production: 'production',
	development: 'development',
	staging: 'development',
};

// Maps Signup Method to Amplitude Event Property
const mapSignupMethod: {
	[key: string]: string;
} = {
	password: 'Email',
	facebook: 'Facebook',
	sso: 'LuxSSO',
	keycloak: 'BLSA-SSO',
};

// Maps User Property to Amplitude Event Property
const userAmpliMap: Record<string, string> = {
	licenceId: 'license id',
	type: 'user type',
	subjects: 'subject',
	firstSubject: 'first subject',
	createdAt: 'signup date',
	teacherType: 'teacher type',
	classYear: 'class year',
	schoolId: 'school id',
	schoolType: 'school type',
	signUpMethod: 'signup method',
	schoolName: 'school name',
	role: 'role',
	federalState: 'state',
	schoolStage: 'school stage',
	schoolStages: 'school stages',
	migrationSource: 'migration source',
	migrationCohort: 'migration cohort',
	migratedDate: 'migrated date',
};

export interface AmpliUserSubscriptionProperties {
	'subscription type'?: string;
	'subscription status'?: string;
	'subscription interval'?: string;
	'subscription end'?: string;
	'subscription start'?: string;
	'promo code'?: string;
	'subscription mmr'?: number;
	'subscription renewed'?: string;
	'school id'?: string;
	'license id'?: number;
}

// Maps Content Property to Amplitude Event Property
const contentAmpliMap: Record<string, string> = {
	classYears: 'content class years',
	id: 'content id',
	title: 'content name',
	publicVideo: 'content public video',
	publisher: 'content publisher',
	schoolTypes: 'content school types',
	subjects: 'content subjects',
	// topics: 'content topics', // TODO: will be added later
	contentType: 'content type',
	collectionPreexistence: 'collection preexistence',
	courseType: 'content course type',
	courseDuration: 'content course duration',
	courseTheme: 'content course theme',
};

// Maps podcast template parameters to Amplitude Event Properties
const podcastTemplateParametersAmpliMap: Record<string, string> = {
	format: 'template parameters format',
	duration: 'template parameters duration',
	language: 'template parameters target language',
};

// Maps Search Property to Amplitude Event Property
const searchAmpliMap: Record<string, string> = {
	subject: 'search filter subject',
	subTopic: 'search filter topics',
	schoolType: 'search filter school types',
	publisher: 'search filter publishers',
	applied: 'search filters applied',
	classYears: 'search filter class years',
	learningFormat: 'search filter educational format',
	teachingMethod: 'search filter educational format',
	teachingFormat: 'search filter exercise format',
	pageContentType: 'search filter material scopes',
	inclusionAdapted: 'search filter differentiation',
	courseType: 'search filter course types',
	courseTheme: 'search filter course themes',
	courseDuration: 'search filter course durations',
};

// Maps User Type to Amplitude Event Property
const mapUserType: {
	[key: number]: string;
} = {
	1: 'Free',
	2: 'Premium',
	3: 'Premium',
	4: 'Premium',
	5: 'Premium',
};

// Maps Teacher Type to Amplitude Event Property
const teacherType = {
	ref: 'Ref',
	regular: 'Regular',
	keineAngabe: 'Keine Angabe',
};

export const premiumPlanSelectedMap: Record<string, string> = {
	licenseId: 'license id',
	subscriptionType: 'subscription type',
	subscriptionInterval: 'subscription interval',
};

export const premiumSubscriptionSubmittedMap: Record<string, string> = {
	licenseId: 'license id',
	subscriptionType: 'subscription type',
	subscriptionInterval: 'subscription interval',
	billingCycle: 'billing cycle',
	promoCode: 'promo code',
	paymentMethod: 'payment method',
	previousUserType: 'previous user type',
	previousLicenceId: 'previous license id',
};

export const amplitude = {
	mapSignupMethod,
	userAmpliMap,
	contentAmpliMap,
	podcastTemplateParametersAmpliMap,
	searchAmpliMap,
	premiumPlanSelectedMap,
	premiumSubscriptionSubmittedMap,
	mapUserType,
	teacherType,
};

export enum amplitudeEvents {
	PageRead = 'Page Read',
	ContentCompleted = 'Content Completed',
	ContentDownloaded = 'Content Downloaded',
	ContentViewed = 'Content Viewed',
	AIAssistantRequestSent = 'AI Assistant Request Sent',
}

export enum amplitudeContentTypes {
	document = 'Document',
	video = 'Video',
	readOnlyDocument = 'Read-only document',
	product = 'Product',
	blankPage = 'Blank page',
	richText = 'Rich text',
	course = 'Course',
	aiImage = 'AI image',
	aiDocument = 'AI document',
	aiAudio = 'AI audio',
}

export const amplitudeSearchSortedByTypes: Record<string, string> = {
	relevance: 'Relevanz',
	createdDate: 'Kürzlich hinzugefügt',
	publisherReleaseDate: 'Erscheinungsdatum',
	popularity: 'Beliebtheit',
};

export const amplitudeSearchSchoolTypes: Record<string, string> = {
	beruflicheschule: 'Berufliche Schule',
	foerderschuleinklusion: 'Förderschule/ Inklusion',
	gesamtschule: 'Gesamtschule',
	grundschule: 'Grundschule',
	gymnasium: 'Gymnasium',
	hauptschule: 'Hauptschule',
	mittlereschule: 'Mittlere Schule',
	realschule: 'Realschule',
};

export type AdditionalContentProperties = {
	contentType: amplitudeContentTypes;
	publicVideo?: string;
	courseTheme?: Course['theme'];
	courseDuration?: Course['duration'];
	courseType?: Course['type'];
};

export type DataType = 'mindItem' | 'product' | 'blankPage' | 'richText';
export interface AmpliContentPayload {
	content: ContentInterface[];
}

export interface AmpliPremiumPlanSelectedPayload {
	'license id'?: number;
	'product name'?: string;
	'subscription interval': string;
	'subscription type': string;
}

export interface AmpliSubscriptionSubmittedPayload {
	'license id': number;
	'subscription interval': string;
	'subscription type': string;
	'billing cycle': string;
	'promo code': string;
	'payment method': string;
	'previous user type'?: number;
	'previous license id'?: number;
}

export const amplitudePaymentMethodTypes: Record<string, string> = {
	sepa: 'SEPA',
	creditCard: 'credit card',
	braintree: 'Paypal',
};

export const amplitudeBilingCicleTypes: Record<string, string> = {
	fullAmount: 'total',
	annually: 'total',
	monthly: 'monthly',
	quarterly: 'quarterly',
};

export interface ModalCollectionItem {
	id: string;
	isSelected: boolean;
}

export interface LicenceInfoByTermLengthType {
	[key: string]: {
		price: string;
		licenceId: string;
	};
}

export interface TrainingLicenceDetails {
	discountPercentage: string;
	period: string;
	price: string;
	licenceId: number;
}

export interface AmpliSubscriptionSubmitted {
	licence: {
		licenceModelId: number;
		isRef: boolean;
		period: string;
	};
	billingCycle: string;
	promoCode: string;
	paymentMethod: string;
	licenceModelId?: number;
	userType?: number;
}

export const ampliSubscriptionType = {
	TEACHER: 'teacher',
	REF: 'ref',
};

export const ampliMigrationSource = {
	LEHRERBUERO: 'lehrerbüro',
};

export enum ampliConversionPath {
	NavigationBar = 'navigation bar',
	MyAccount = 'my account',
	DownloadLimitReached = 'download limit reached',
	PremiumContent = 'premium content',
	DocumentOverlay = 'document overlay',
	ContentDownload = 'content download',
	PremiumInfoPopup = 'premium info popup',
	CourseCoverPage = 'course cover page',
	CertificatePage = 'certificate page',
}

export interface AmpliPremiumPlanSelected {
	isTrainee: boolean;
	traineeLicenceDetails: TrainingLicenceDetails;
	selectedTermLength: string;
	licenceDetailsByTermLength: LicenceInfoByTermLengthType;
}

export const ampliSubscriptionPageSource = {
	LAST_CONFIRMATION: 'last confirmation',
	PAUSE_SUGGESTION: 'pause suggestion',
	DIVERSE_MATERIAL_RECOMMENDATION: 'diverse material recommendation',
};

// Local storage key for dismissing all hints and dialogs in the cypress test environment.
export const dismissAllHintsAndDialogsKey = 'dismiss-all-hints-and-dialogs';

// When adding new hints, make sure to update the
// dismissAllHintsAndDialogs function in the utils.js file in cypress/support/utils.js
export const onboardingHints = {
	newUIBanner: 'new-ui-banner',
	newUISmallBannerStage3: 'new-ui-small-banner-stage-3',
	feedBackBanner: 'feedback-banner',
	newFiltersFeedback: 'new-filters-feedback',
	newReaderFeedback: {
		isClosed: 'new-reader-feedback-is-closed',
		isFirstVisit: 'new-reader-feedback-is-first-visit',
		isFirstSession: 'new-reader-feedback-is-first-session',
	},
	lernsetsTopBarCTA: 'lernsets-topbar-cta',
	lernsetsReaderCTA: 'lernsets-reader-cta',
	lernsetsOverviewEdit: 'lernsets-overview-edit',
	lernsetsShareButton: 'lernsets-share-button',
	newReaderDesktop: 'new-reader-desktop',
	newReaderMobile: 'new-reader-mobile',
	courseOnboardingHint: 'course-onboarding-hint',
	aiOnlyWelcomeDialog: 'ai-only-welcome-dialog',
	lbReOnboardingDialog: 'lb-reonboarding',
	mukiBudeFeedback: {
		notShowAgain: 'muki-bude-feedback-not-show-again',
	},
	aiOnlyFeedback: {
		notShowAgain: 'ai-only-feedback-not-show-again',
		sessionDismissed: 'ai-only-feedback-session-dismissed',
	},
	aiAssistant: {
		didAutoOpenHappen: 'reader-ai-assistant-did-auto-open-happen-v2',
		chatHistoryButton: 'reader-ai-assistant-chat-history-button',
		entryHint: 'reader-ai-assistant-entry',
		historyHint: 'mukibude-history',
		readerHint: 'reader-ai-assistant-reader',
		loomVideo: 'reader-ai-assistant-loom-video',
		didFirstSessionHappen: 'reader-ai-assistant-did-first-session-happen-v2',
		sessionCounter: 'reader-ai-assistant-session-counter',
		hint: 'reader-ai-assistant',
		homeButton: 'reader-ai-assistant-home-button',
		pageSelector: 'reader-ai-assistant-page-selector',
		textEditor: 'reader-ai-assistant-text-editor',
		artifactGenerationOnboardingAlertSeen: 'ai-assistant-artifact-generation-onboarding-alert-seen',
		imageGeneratorHint: 'image-generator-hint',
	},
} as const;

// Union of every hint id declared in onboardingHints. Typing a hint id to this
// blocks stray string literals: a new id must be added to onboardingHints first.
// Example: id='course-onboarding-hint' only compiles because courseOnboardingHint
// lives above.
type OnboardingHintLeaf<T> = T extends string
	? T
	: { [K in keyof T]: OnboardingHintLeaf<T[K]> }[keyof T];
export type OnboardingHintId = OnboardingHintLeaf<typeof onboardingHints>;

export const onboardingHintsMaxTimes = {
	newUISmallBannerStage3: 3,
};

export const zIndex = {
	alert: 1500,
	addMenu: 1501,
	searchResultsTab: 2,
	productPageDesignSwitch: 5,
	muiProgress: 1500,
};

export const COURSE_REGISTRATION_STATUS = {
	NONE: 'none',
	PROGRESS: 'progress',
	SUCCESS: 'success',
};

export const meinUnterrichtSchulePackage = {
	packageLicenceIds: 577,
	name: 'meinUnterricht Schule',
	description: 'For institutions',
	price: 'Auf Anfrage',
	duration: '',
	benefits: [
		{
			title: 'Access',
			features: [
				{
					text: 'A personal access for each teacher',
					isHighlighted: false,
				},
				{
					text: 'Unlimited online reading',
					isHighlighted: false,
				},
				{
					text: 'Download 100 materials per month per teacher',
					isHighlighted: false,
				},
			],
		},
		{
			title: 'Costs',
			features: [
				{
					text: 'Up to 75% discount for schools',
					isHighlighted: false,
				},
				{
					text: 'No private costs for teachers',
					isHighlighted: false,
				},
			],
		},
		{
			title: 'Funding',
			features: [
				{
					text: 'Usually covered by the school authority',
					isHighlighted: false,
				},
				{
					text: 'Easily fundable through regional and nationwide school development programs',
					isHighlighted: false,
				},
			],
		},
	],
};

export const packageLicenceIds = {
	meinUnterrichtPro: 1320,
	meinUnterrichtProForTrainees: 410,
	meinUnterricht360: 1720,
	meinUnterricht360ForDocumentUsers: 1730,
	meineFortbildung: 1520,
};

export const newPricingPackageLicenceIds = {
	meinUnterrichtProKI: 2320,
	meinUnterricht360KI: 2720,
	meinUnterrichtProTrial: 3830,
	meinUnterrichtProMonthly: 9999,
};

export const numberOfSlides = {
	xl: 5,
	lg: 4,
	md: 3,
	sm: 1.5,
	xs: 1.2,
	xxs: 1,
};

export const roleAvatarMap = {
	'Referendar:in': '🎓',
	'Ausgebildete Lehrkraft': '✏️',
	Aushilfslehrkraft: '🔄',
	'Quereinsteiger:in': '🚀',
	'Tutor:in': '🎯',
	'Student:in': '️️🏛️',
	'Schulkoordinator:in': '🏫',
	Sonstiges: '🌍',
};

export const gradeAvatarMap = {
	Primarstufe: '🧒🏻',
	'Sekundarstufe I': '🎒',
	'Sekundarstufe II': '🎓',
	Sonstiges: '🌍',
};

export const schoolLicences = {
	models: [
		{
			id: 557,
			shortName: 'Bibliothek',
			fullName: 'Bibliothek (Unterrichtsmaterialien)',
			zuoraLabel: 'bibliothek',
			ampliName: 'mU Pro',
		},
		{
			id: 567,
			shortName: 'Fortbildungen',
			fullName: 'Fortbildungen (Kurse)',
			zuoraLabel: 'course',
			ampliName: 'meine Fortbildung',
		},
		{
			id: 577,
			shortName: 'Bibliothek & Fortbildungen',
			fullName: 'Bibliothek & Fortbildungen (Unterrichtsmaterialien & Kurse)',
			zuoraLabel: 'kombi',
			ampliName: 'mU 360',
		},
	],
	MU_PRO: 557,
	COURSE: 567,
	KOMBI: 577,
};

export const getProductByLicenceModelId = (licenceModelId: number) => {
	return schoolLicences.models.find((m) => m.id === licenceModelId);
};

export const getProductByShortName = (name: string) => {
	return schoolLicences.models.find((m) => m.shortName === name);
};

export const getProductByFullName = (name: string) => {
	return schoolLicences.models.find((m) => m.fullName === name);
};

export const cancellationReasons = [
	'The price is too high for me',
	'I am now using another platform or a different AI tool',
	'The selection of content does not meet my needs',
	'I am now using meinUnterricht through my school or university',
	'I am no longer teaching',
	'I am dissatisfied with the functionality',
];

export const manageSubscriptionSteps = {
	manage: 'manageSubscription',
	pause: 'pauseSubscription',
	cancel: 'cancelSubscription',
	withdraw: 'withdrawSubscription',
};

export const campaign = {
	schulstart30: 'schulstart25-30%',
	earlyBird2026: 'early-bird-2026',
	searchRelevanceFeedback: 'search-relevance-feedback',
	mukiOnboardingWelcome: 'muki-onboarding-welcome',
	mukiHintAiButton: 'muki-hint-aibutton',
	mukiOnboardingReader: 'muki-onboarding-reader',
	mukiBudeChatHistory: 'mukibude-chat-history',
	reOnboardingDialog: 'reonboarding-dialog',
	aiOnlyWelcomeMessage: 'ai-only-welcome-message',
	imageGeneratorTool: 'image-generator-tool',
	notesOnboarding: 'notes-onboarding',
	dashboardPodcastPromo: 'dashboard-podcast-promo',
};

// Values used as the `page source` property on AI Assistant Amplitude events.
// Centralised so the new surfaces (Meine Inhalte, Verlauf) don't drift from the
// pre-existing 'reader' / 'mukibude' strings produced by getAIAssistantPageSource.
export const aiAssistantPageSource = {
	reader: 'reader',
	mukibude: 'mukibude',
	myContent: 'my content',
	chatHistory: 'chat history',
	dashboard: 'dashboard',
} as const;

export type AiAssistantPageSource =
	(typeof aiAssistantPageSource)[keyof typeof aiAssistantPageSource];

// Values used as the `page source` property on Content Viewed Amplitude events
// (SEAR-683) — the exact strings the taxonomy expects. Pathname mapping lives
// in utils/pageSource.ts.
export const contentViewedPageSource = {
	dashboard: 'dashboard',
	search: 'search',
	favourites: 'favourites',
	downloads: 'downloads',
	myCollections: 'my collections',
	curatedCollections: 'curated collections',
	product: 'product',
	reader: 'reader',
} as const;

export type ContentViewedPageSource =
	(typeof contentViewedPageSource)[keyof typeof contentViewedPageSource];

// Values used as the `dashboard section` property on Content Viewed events —
// the exact strings the taxonomy expects, one per dashboard row.
// `didactic materials` and `curated collections` are reserved in the tracking
// plan for future rows and are not wired to any row yet.
export const dashboardSection = {
	worksheets: 'worksheets',
	userSubject: 'user subject',
	studyflixVideos: 'studyflix videos',
	recentlyViewed: 'recently viewed',
	favorites: 'favorites',
	courses: 'courses',
	didacticMaterials: 'didactic materials',
	curatedCollections: 'curated collections',
} as const;

export type DashboardSection = (typeof dashboardSection)[keyof typeof dashboardSection];

export const muiInputFieldBorderStyle = {
	'& .MuiOutlinedInput-notchedOutline': {
		border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
		borderRadius: borderRadius.sm,
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
	},
	'&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
		border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
	},
	border: 'none',
};

export const filterChipStyle = {
	gap: 8,
	height: 32,
};

export enum READER_ZOOM_FACTOR {
	Fifty = 50,
	SeventyFive = 75,
	OneHundred = 100,
}

export const searchAlgorithm = {
	BM25_BASELINE: 'bm25-baseline',
	BM25: 'bm25',
	VECTOR: 'vector',
	HYBRID: 'hybrid',
	RERANKED_HYBRID: 'reranked_hybrid',
};

export const teachingFormats = [
	{ key: 'lesespur', label: 'Reading Trail' },
	{ key: 'raetsel_kreuzwortraetsel', label: 'Puzzle / Crossword' },
	{ key: 'logical_knobelaufgabe', label: 'Logic Puzzle' },
	{ key: 'diagrammarbeit', label: 'Diagram Work' },
	{ key: 'textaufgaben', label: 'Text Problems' },
	{ key: 'steckbrief', label: 'Fact Sheet' },
	{ key: 'zuordnung', label: 'Matching' },
	{ key: 'offene_aufgaben', label: 'Open Tasks' },
	{ key: 'tabellenarbeit', label: 'Table Work' },
	{ key: 'lueckentext', label: 'Fill in the Blanks' },
	{ key: 'multiple_choice', label: 'Multiple Choice' },
	{ key: 'quiz', label: 'Quiz' },
	{ key: 'richtig_falsch', label: 'True / False' },
	{ key: 'diktat', label: 'Dictation' },
	{ key: 'escape_room', label: 'Escape Room' },
	{ key: 'bingo', label: 'Bingo' },
	{ key: 'rechenaufgaben', label: 'Math Problems' },
] as const;

export const teachingMethods = [
	{ key: 'frontalunterricht', label: 'Frontal Teaching' },
	{ key: 'gruppenarbeit', label: 'Group Work' },
	{ key: 'partnerarbeit_tandem', label: 'Partner Work / Tandem' },
	{ key: 'einzelarbeit', label: 'Individual Work' },
	{ key: 'projektarbeit', label: 'Project Work' },
	{ key: 'stationenlernen', label: 'Station Learning' },
	{ key: 'freiarbeit', label: 'Free Work' },
	{ key: 'werkstattunterricht', label: 'Workshop Teaching' },
	{ key: 'lernzirkel', label: 'Learning Circle' },
	{ key: 'rollenspiel', label: 'Role Play' },
	{ key: 'diskussion', label: 'Discussion' },
	{ key: 'versuche', label: 'Experiments' },
	{ key: 'vortrag', label: 'Presentation' },
	{ key: 'lernspiel', label: 'Learning Game' },
] as const;

export const pageContentTypes = [
	{ key: 'arbeitsblatt', label: 'Worksheet' },
	{ key: 'loesungen', label: 'Solutions' },
	{ key: 'test_pruefung', label: 'Test / Exam' },
	{ key: 'visualisierung', label: 'Visualization' },
	{ key: 'didaktische_hinweise', label: 'Didactic Notes' },
	{ key: 'methodische_hinweise', label: 'Methodological Notes' },
	{ key: 'fachliche_hinweise', label: 'Subject Notes' },
	{ key: 'unterrichtsverlaufsplan', label: 'Lesson Plan' },
] as const;

export const inclusionAdapted = [{ key: 'inclusionAdapted', label: 'Inclusion Adapted' }] as const;

export const searchFilterKeys = [
	'subject',
	'classYear',
	'pageContentType',
	'inclusionAdapted',
	'schoolType',
	'publisher',
	'teachingFormat',
	'teachingMethod',
] as const;

export const searchVisibleFilterKeys = [
	'subject',
	'classYear',
	'pageContentType',
	'schoolType',
] as const;

export const filterLabels: Record<string, string> = {
	classYear: 'Class years',
	subject: 'Subject',
	publisher: 'Publisher',
	schoolType: 'SchoolType',
	workload: 'Workload',
	teachingFormat: 'Teaching Format',
	teachingMethod: 'Teaching method',
	learningFormat: 'Learning format',
	contentType: 'Content type',
	pageContentType: 'Content type',
	inclusionAdapted: 'Differentiation',
	subTopic: 'SubTopic',
	courseTheme: 'Course theme',
	courseType: 'Course type',
	courseDuration: 'Course duration',
};

export enum SubscriptionCancellationType {
	RevocationRequested = 'revocation requested',
	CancellationRequested = 'cancellation requested',
}
